// coeditBroadcastRegistry.js — bridges LIVE CO-EDIT's outbound "op" send path
// to code that has no direct handle on the socket.
//
// `useWheelCoedit`'s `broadcastEdit` is a component-scoped closure (the socket
// is opened/closed with the builder's CoeditStatus mount), but the actual
// local-edit COMMIT points (e.g. the Details tab's wheel-name/subtitle save)
// live in a completely different part of the component tree and have no prop
// path to that closure. Rather than thread `broadcastEdit` through every
// intermediate component (or duplicate a socket per edit surface), the hook
// registers its current `broadcastEdit` here on mount/update, and anything
// else (redux middleware, in practice — see
// ../redux/middleware/coeditBroadcastMiddleware.js) reads it back.
//
// Deliberately NOT a React context: the reader here is redux middleware, which
// runs outside the component tree and cannot consume a context.
let current = null;

export function registerCoeditBroadcast(entry) {
  current = entry;
  return () => {
    if (current === entry) current = null;
  };
}

export function getCoeditBroadcast() {
  return current;
}

// T22.104: tell the room a save landed (`coedit_saved`) so watch-only peers
// (the View Wheel page) refetch the persisted wheel. Without this the server's
// `saved` relay was never fed and a teammate's view stayed stale until reload.
export function announceCoeditSaved() {
  const session = current;
  if (!session || typeof session.broadcastSaved !== "function") return;
  try {
    const version =
      typeof session.getVersion === "function" ? session.getVersion() : 0;
    session.broadcastSaved(version);
  } catch (e) {
    /* a co-edit hiccup must never break a save */
  }
}

// T22.125: the builder listens for a TEAMMATE's save ("coedit_saved") so it can
// pick up segments they added (a structural add is not a live op). Same
// single-slot, component-scoped shape as the broadcast session above.
let remoteSavedListener = null;

export function registerCoeditRemoteSavedListener(listener) {
  remoteSavedListener = listener;
  return () => {
    if (remoteSavedListener === listener) remoteSavedListener = null;
  };
}

export function notifyCoeditRemoteSaved(message) {
  const listener = remoteSavedListener;
  if (typeof listener !== "function") return;
  try {
    listener(message);
  } catch (e) {
    /* a listener error must never break the socket */
  }
}
